import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { COLORS } from '../../utils/constants';
import { Save, Trash2, Plus, X, Eye } from 'lucide-react';

const C = COLORS;

const QuizForm = ({ defaultType, basePath }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = !!id;

  let detectedType = defaultType;
  let detectedBasePath = basePath;
  if (!detectedBasePath) {
    if (location.pathname.includes('/pal/practice')) { detectedBasePath = '/pal/practice'; detectedType = detectedType || 'Practice'; }
    else if (location.pathname.includes('/pal/tests')) { detectedBasePath = '/pal/tests'; detectedType = detectedType || 'Tests'; }
    else if (location.pathname.includes('/pal/learning')) { detectedBasePath = '/pal/learning'; detectedType = detectedType || 'Learning'; }
    else if (location.pathname.includes('/pal/homework')) { detectedBasePath = '/pal/homework'; detectedType = detectedType || 'Homework'; }
    else { detectedBasePath = '/pal/practice'; detectedType = detectedType || 'Practice'; }
  }

  const isInitialTest = (detectedType || '').toLowerCase() === 'tests' || (detectedType || '').toLowerCase() === 'test';
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    question_type: 'Board Exams',
    board_id: '',
    exam_id: '',
    class_id: '',
    subject_id: '',
    status: 'active',
    quiz_type: detectedType || 'Practice',
    duration: isInitialTest ? 40 : ''
  });

  const [boards, setBoards] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  
  // Modal state for searching questions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterTopicId, setFilterTopicId] = useState('');
  const [filterSubTopicId, setFilterSubTopicId] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [modalFilters, setModalFilters] = useState({
    question_type: '',
    board_id: '',
    exam_id: '',
    class_id: '',
    subject_id: ''
  });
  
  // Modal state for viewing a single question
  const [viewingQuestion, setViewingQuestion] = useState(null);
  
  const [availableQuestions, setAvailableQuestions] = useState([]);
  
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [tempSelectedQuestions, setTempSelectedQuestions] = useState([]);
  const [selectedQuizQuestionIds, setSelectedQuizQuestionIds] = useState([]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [b, ex, c, s, t, st] = await Promise.all([
          api.get('/config/board'),
          api.get('/config/exam'),
          api.get('/config/class'),
          api.get('/config/subject'),
          api.get('/config/topic'),
          api.get('/config/sub-topic')
        ]);
        setBoards(b.data.filter(x => x.status === 'active'));
        setExams(ex.data.filter(x => x.status === 'active'));
        setClasses(c.data.filter(x => x.status === 'active'));
        setSubjects(s.data.filter(x => x.status === 'active'));
        setTopics(t.data.filter(x => x.status === 'active'));
        setSubTopics(st.data.filter(x => x.status === 'active'));
      } catch (err) {
        toast.error('Failed to load configurations');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (isEdit) {
      const fetchQuiz = async () => {
        try {
          const res = await api.get(`/quizzes/${id}`);
          const q = res.data;
          setFormData({
            name: q.name,
            start_date: q.start_date.split('T')[0],
            end_date: q.end_date.split('T')[0],
            question_type: q.question_type || 'Board Exams',
            board_id: q.board_id || '',
            exam_id: q.exam_id || '',
            class_id: q.class_id || '',
            subject_id: q.subject_id || '',
            status: q.status,
            quiz_type: q.quiz_type || 'Practice',
            duration: q.duration !== undefined && q.duration !== null ? q.duration : ''
          });
          
          if (q.questions) {
            setSelectedQuestions(q.questions);
          }
        } catch (err) {
          toast.error('Failed to load quiz');
        }
      };
      fetchQuiz();
    }
  }, [id, isEdit]);

  useEffect(() => {
    const fetchQuestionsForTopic = async () => {
      if (filterTopicId && filterSubTopicId) {
        try {
          const res = await api.get(`/questions?topic_id=${filterTopicId}&sub_topic_id=${filterSubTopicId}`);
          setAvailableQuestions(res.data);
        } catch (err) {
          toast.error('Failed to fetch available questions');
        }
      } else {
        setAvailableQuestions([]);
      }
    };
    fetchQuestionsForTopic();
  }, [filterTopicId, filterSubTopicId]);

  const openModal = () => {
    setTempSelectedQuestions(selectedQuestions);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };
  
  const closeViewModal = () => {
    setViewingQuestion(null);
  };

  const toggleTempQuestionSelection = (question) => {
    setTempSelectedQuestions(prev => {
      const exists = prev.find(q => q.id === question.id);
      if (exists) {
        return prev.filter(q => q.id !== question.id);
      } else {
        return [...prev, question];
      }
    });
  };

  const handleModalQuestionTypeChange = (val) => {
    setModalFilters(prev => ({
      ...prev,
      question_type: val,
      board_id: '',
      exam_id: ''
    }));
    setFilterTopicId('');
    setFilterSubTopicId('');
  };

  const handleModalBoardChange = (val) => {
    setModalFilters(prev => ({ ...prev, board_id: val }));
    setFilterTopicId('');
    setFilterSubTopicId('');
  };

  const handleModalExamChange = (val) => {
    setModalFilters(prev => ({ ...prev, exam_id: val }));
    setFilterTopicId('');
    setFilterSubTopicId('');
  };

  const handleModalClassChange = (val) => {
    setModalFilters(prev => ({ ...prev, class_id: val }));
    setFilterTopicId('');
    setFilterSubTopicId('');
  };

  const handleModalSubjectChange = (val) => {
    setModalFilters(prev => ({ ...prev, subject_id: val }));
    setFilterTopicId('');
    setFilterSubTopicId('');
  };

  const confirmSelection = () => {
    setSelectedQuestions(tempSelectedQuestions);
    setSelectedQuizQuestionIds(prev => prev.filter(id => tempSelectedQuestions.some(q => q.id === id)));
    closeModal();
  };

  const toggleQuizQuestionSelection = (qId) => {
    setSelectedQuizQuestionIds(prev => 
      prev.includes(qId) ? prev.filter(id => id !== qId) : [...prev, qId]
    );
  };

  const handleSelectAllQuizQuestions = () => {
    if (selectedQuizQuestionIds.length === selectedQuestions.length) {
      setSelectedQuizQuestionIds([]);
    } else {
      setSelectedQuizQuestionIds(selectedQuestions.map(q => q.id));
    }
  };

  const removeSelectedQuizQuestions = () => {
    if (selectedQuizQuestionIds.length === 0) return;
    const count = selectedQuizQuestionIds.length;
    if (!window.confirm(`Are you sure you want to remove ${count} selected question${count > 1 ? 's' : ''} from this quiz?`)) {
      return;
    }
    setSelectedQuestions(prev => prev.filter(q => !selectedQuizQuestionIds.includes(q.id)));
    setSelectedQuizQuestionIds([]);
    toast.success(`Removed ${count} question${count > 1 ? 's' : ''} from quiz`);
  };

  const removeSelectedQuestion = (qId) => {
    setSelectedQuestions(prev => prev.filter(q => q.id !== qId));
    setSelectedQuizQuestionIds(prev => prev.filter(id => id !== qId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a Quiz Name');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select Start Date and End Date');
      return;
    }
    if (formData.question_type !== 'Compititive' ? !formData.board_id : !formData.exam_id) {
      toast.error(`Please select a ${formData.question_type !== 'Compititive' ? 'Board' : 'Exam'}`);
      return;
    }
    if (!formData.class_id) {
      toast.error('Please select a Class');
      return;
    }
    if (!formData.subject_id) {
      toast.error('Please select a Subject');
      return;
    }
    const isTestQuiz = (formData.quiz_type || '').toLowerCase() === 'tests' || (formData.quiz_type || '').toLowerCase() === 'test';
    if (isTestQuiz && (!formData.duration || Number(formData.duration) <= 0)) {
      toast.error('Please enter a valid duration (in minutes) for the test');
      return;
    }
    if (selectedQuestions.length === 0) {
      toast.error('Please add at least one question to the quiz');
      return;
    }
    try {
      const payload = { 
        ...formData, 
        duration: isTestQuiz && formData.duration ? parseInt(formData.duration, 10) : null,
        topic_id: null,
        sub_topic_id: null,
        question_ids: selectedQuestions.map(q => q.id) 
      };
      if (isEdit) {
        await api.put(`/quizzes/${id}`, payload);
        toast.success(`${detectedType || 'Item'} updated successfully`);
        navigate(detectedBasePath);
      } else {
        const res = await api.post('/quizzes', payload);
        toast.success(`${detectedType || 'Item'} created successfully`);
        navigate(detectedBasePath);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save quiz');
    }
  };

  const filteredAvailableQuestions = availableQuestions.filter(q => {
    if (filterLevel && q.level !== filterLevel) return false;
    return true;
  });

  const areAllModalQuestionsSelected = 
    filteredAvailableQuestions.length > 0 && 
    filteredAvailableQuestions.every(q => tempSelectedQuestions.some(sq => sq.id === q.id));

  const areSomeModalQuestionsSelected = 
    filteredAvailableQuestions.some(q => tempSelectedQuestions.some(sq => sq.id === q.id)) && 
    !areAllModalQuestionsSelected;

  const handleSelectAllModalQuestions = () => {
    if (areAllModalQuestionsSelected) {
      const filteredIds = new Set(filteredAvailableQuestions.map(q => q.id));
      setTempSelectedQuestions(prev => prev.filter(q => !filteredIds.has(q.id)));
    } else {
      setTempSelectedQuestions(prev => {
        const existingIds = new Set(prev.map(q => q.id));
        const toAdd = filteredAvailableQuestions.filter(q => !existingIds.has(q.id));
        return [...prev, ...toAdd];
      });
    }
  };

  // Helper to safely get topic name since existing quiz questions might lack topic_name in some joins
  const getTopicName = (q) => q.topic_name || topics.find(t => t.id === q.topic_id)?.name || 'Unknown Topic';
  const getSubTopicName = (q) => q.sub_topic_name || subTopics.find(st => st.id === q.sub_topic_id)?.name || 'Unknown Sub Topic';

  const isTestQuiz = (formData.quiz_type || '').toLowerCase() === 'tests' || (formData.quiz_type || '').toLowerCase() === 'test';

  return (
    <AppLayout title={isEdit ? `Edit ${detectedType || 'Item'}` : `Add ${detectedType || 'Item'}`}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 60 }}>
        
        {/* Quiz Metadata */}
        <div style={{ background: '#fff', padding: 28, borderRadius: 16, border: `1px solid ${C.g200}` }}>
          <h3 style={{ margin: '0 0 20px', fontSize: 16, color: C.g900 }}>Quiz Details</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Quiz Name</label>
              <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Quiz Type</label>
              <select 
                value={formData.quiz_type} 
                onChange={e => {
                  const newType = e.target.value;
                  const isNowTest = newType.toLowerCase() === 'tests' || newType.toLowerCase() === 'test';
                  setFormData(prev => ({
                    ...prev, 
                    quiz_type: newType,
                    duration: isNowTest ? (prev.duration || 40) : prev.duration
                  }));
                }} 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
              >
                <option value="Practice">Practice</option>
                <option value="Tests">Tests</option>
                <option value="Learning">Learning</option>
                <option value="Homework">Homework</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Status</label>
              <select value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isTestQuiz ? '1fr 1fr 1fr' : '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Start Date</label>
              <input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>End Date</label>
              <input type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }} required />
            </div>
            {isTestQuiz && (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>
                  Duration (in minutes) <span style={{ color: C.danger }}>*</span>
                </label>
                <input 
                  type="number" 
                  min="1" 
                  max="600" 
                  placeholder="e.g. 40" 
                  value={formData.duration} 
                  onChange={e => setFormData({ ...formData, duration: e.target.value === '' ? '' : e.target.value })} 
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }} 
                  required={isTestQuiz} 
                />
              </div>
            )}
          </div>

          {/* Row 3: Question Type, Board/Exam, Class, Subject */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Type</label>
              <select 
                value={formData.question_type} 
                onChange={e => {
                  const val = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    question_type: val,
                    board_id: '',
                    exam_id: ''
                  }));
                }} 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} 
                required
              >
                <option value="Board Exams">Board Exams</option>
                <option value="Compititive">Compititive</option>
              </select>
            </div>
            
            {formData.question_type !== 'Compititive' ? (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Board</label>
                <select 
                  value={formData.board_id} 
                  onChange={e => setFormData({ ...formData, board_id: e.target.value })} 
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} 
                  required
                >
                  <option value="">Select Board</option>
                  {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Exam</label>
                <select 
                  value={formData.exam_id} 
                  onChange={e => setFormData({ ...formData, exam_id: e.target.value })} 
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} 
                  required
                >
                  <option value="">Select Exam</option>
                  {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
            )}

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Class</label>
              <select 
                value={formData.class_id} 
                onChange={e => setFormData({ ...formData, class_id: e.target.value })} 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} 
                required
              >
                <option value="">Select Class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Subject</label>
              <select 
                value={formData.subject_id} 
                onChange={e => setFormData({ ...formData, subject_id: e.target.value })} 
                style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }} 
                required
              >
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Selected Questions Summary */}
        <div style={{ background: '#fff', padding: 28, borderRadius: 16, border: `1px solid ${C.g200}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 16, color: C.g900 }}>Questions Included in Quiz</h3>
              <p style={{ margin: 0, fontSize: 13, color: C.g500 }}>
                Total Questions: <strong style={{ color: C.g800, fontSize: 14 }}>{selectedQuestions.length}</strong>
                {selectedQuizQuestionIds.length > 0 && (
                  <span style={{ color: '#DC2626', fontWeight: 600, marginLeft: 10 }}>
                    • {selectedQuizQuestionIds.length} marked to delete
                  </span>
                )}
              </p>
            </div>
            <button 
              type="button" 
              onClick={openModal} 
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: C.primaryGhost, color: C.primary, border: `1px solid ${C.primary}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              <Plus size={16} /> Add Question
            </button>
          </div>

          {/* Bulk Selection Toolbar */}
          {selectedQuestions.length > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '12px 16px', 
              background: selectedQuizQuestionIds.length > 0 ? '#FEF2F2' : '#F8FAFC', 
              borderRadius: 10, 
              border: `1px solid ${selectedQuizQuestionIds.length > 0 ? '#FECACA' : C.g200}`, 
              marginBottom: 16,
              transition: 'background-color 0.2s, border-color 0.2s'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.g700, userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={selectedQuestions.length > 0 && selectedQuizQuestionIds.length === selectedQuestions.length}
                  ref={el => {
                    if (el) {
                      el.indeterminate = selectedQuizQuestionIds.length > 0 && selectedQuizQuestionIds.length < selectedQuestions.length;
                    }
                  }}
                  onChange={handleSelectAllQuizQuestions}
                  style={{ width: 17, height: 17, accentColor: C.primary, cursor: 'pointer' }}
                />
                Select All ({selectedQuestions.length})
              </label>

              {selectedQuizQuestionIds.length > 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>
                    {selectedQuizQuestionIds.length} question{selectedQuizQuestionIds.length > 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedQuizQuestionIds([])}
                    style={{ padding: '6px 12px', background: '#fff', color: C.g600, border: `1px solid ${C.g300}`, borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                  >
                    Deselect All
                  </button>
                  <button
                    type="button"
                    onClick={removeSelectedQuizQuestions}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    <Trash2 size={15} /> Delete Selected ({selectedQuizQuestionIds.length})
                  </button>
                </div>
              ) : (
                <span style={{ fontSize: 13, color: C.g500 }}>
                  Total: <strong>{selectedQuestions.length}</strong> questions
                </span>
              )}
            </div>
          )}

          {selectedQuestions.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {selectedQuestions.map((q, idx) => {
                const isCardChecked = selectedQuizQuestionIds.includes(q.id);
                return (
                  <div 
                    key={q.id || idx} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 14, 
                      padding: 16, 
                      border: `1px solid ${isCardChecked ? C.primary : C.g200}`, 
                      borderRadius: 12, 
                      background: isCardChecked ? '#F0F9FF' : '#fafafa',
                      transition: 'border-color 0.15s, background-color 0.15s'
                    }}
                  >
                    <div style={{ paddingTop: 2 }}>
                      <input
                        type="checkbox"
                        checked={isCardChecked}
                        onChange={() => toggleQuizQuestionSelection(q.id)}
                        style={{ width: 17, height: 17, accentColor: C.primary, cursor: 'pointer' }}
                        title="Select question"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 8px', fontSize: 14, color: C.g800, fontWeight: 500 }}>
                        <span style={{ fontWeight: 700, color: C.primary, marginRight: 6 }}>Q{idx + 1}.</span>
                        {q.question_text}
                      </p>
                      <div style={{ display: 'flex', gap: 16, fontSize: 12, color: C.g500, flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          Topic: <strong style={{ color: C.g700 }}>{getTopicName(q)}</strong>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          Sub Topic: <strong style={{ color: C.g700 }}>{getSubTopicName(q)}</strong>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          Level: <strong style={{ color: C.g700 }}>{q.level}</strong>
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button type="button" onClick={() => setViewingQuestion(q)} style={{ background: C.primaryGhost, color: C.primary, border: 'none', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} title="View Question">
                        <Eye size={16} />
                      </button>
                      <button type="button" onClick={() => removeSelectedQuestion(q.id)} style={{ background: '#FEE2E2', color: '#EF4444', border: 'none', width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} title="Remove Question">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '36px 20px', border: `1px dashed ${C.g300}`, borderRadius: 12, background: '#FAFAFA' }}>
              <p style={{ margin: '0 0 8px', fontSize: 14, color: C.g600, fontWeight: 500 }}>No questions added yet</p>
              <p style={{ margin: 0, fontSize: 13, color: C.g400 }}>Click &quot;Add Question&quot; to filter and select questions for this quiz.</p>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" onClick={() => navigate(detectedBasePath)} style={{ padding: '12px 24px', background: C.g100, color: C.g700, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="submit" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <Save size={18} /> {isEdit ? `Update ${detectedType || 'Item'}` : `Save ${detectedType || 'Item'}`}
          </button>
        </div>
      </form>

      {/* Modal for Finding and Adding Questions */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 960, borderRadius: 16, display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            
            {/* Modal Header */}
            <div style={{ padding: 24, borderBottom: `1px solid ${C.g200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, color: C.g900 }}>Find & Add Questions</h2>
                <p style={{ margin: 0, fontSize: 13, color: C.g500 }}>Filter by Question Type, Board/Exam, Class, Subject, Topic, and Sub Topic to find questions.</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button 
                  type="button" 
                  onClick={() => {
                    setModalFilters({ question_type: '', board_id: '', exam_id: '', class_id: '', subject_id: '' });
                    setFilterTopicId('');
                    setFilterSubTopicId('');
                    setFilterLevel('');
                  }}
                  style={{ background: 'none', border: 'none', color: C.primary, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 8px' }}
                >
                  Reset Filters
                </button>
                <button onClick={closeModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <X color={C.g500} size={24} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
              
              {/* Row 1: The 4 Dropdowns (Question Type, Board/Exam, Class, Subject) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Type</label>
                  <select 
                    value={modalFilters.question_type} 
                    onChange={e => handleModalQuestionTypeChange(e.target.value)} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">All Question Types</option>
                    <option value="Board Exams">Board Exams</option>
                    <option value="Compititive">Compititive</option>
                  </select>
                </div>
                
                {modalFilters.question_type === 'Compititive' ? (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Exam</label>
                    <select 
                      value={modalFilters.exam_id} 
                      onChange={e => handleModalExamChange(e.target.value)} 
                      style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                    >
                      <option value="">All Exams</option>
                      {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                    </select>
                  </div>
                ) : modalFilters.question_type === 'Board Exams' ? (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Board</label>
                    <select 
                      value={modalFilters.board_id} 
                      onChange={e => handleModalBoardChange(e.target.value)} 
                      style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                    >
                      <option value="">All Boards</option>
                      {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Board / Exam</label>
                    <select 
                      value={
                        modalFilters.board_id ? `board:${modalFilters.board_id}` : 
                        modalFilters.exam_id ? `exam:${modalFilters.exam_id}` : ''
                      } 
                      onChange={e => {
                        const val = e.target.value;
                        if (!val) {
                          setModalFilters(prev => ({ ...prev, board_id: '', exam_id: '' }));
                        } else if (val.startsWith('board:')) {
                          setModalFilters(prev => ({ ...prev, board_id: val.replace('board:', ''), exam_id: '' }));
                        } else if (val.startsWith('exam:')) {
                          setModalFilters(prev => ({ ...prev, exam_id: val.replace('exam:', ''), board_id: '' }));
                        }
                        setFilterTopicId('');
                        setFilterSubTopicId('');
                      }} 
                      style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                    >
                      <option value="">All Boards & Exams</option>
                      {boards.length > 0 && (
                        <optgroup label="Boards">
                          {boards.map(b => <option key={`b-${b.id}`} value={`board:${b.id}`}>{b.name}</option>)}
                        </optgroup>
                      )}
                      {exams.length > 0 && (
                        <optgroup label="Exams">
                          {exams.map(ex => <option key={`e-${ex.id}`} value={`exam:${ex.id}`}>{ex.name}</option>)}
                        </optgroup>
                      )}
                    </select>
                  </div>
                )}

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Class</label>
                  <select 
                    value={modalFilters.class_id} 
                    onChange={e => handleModalClassChange(e.target.value)} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">All Classes</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Subject</label>
                  <select 
                    value={modalFilters.subject_id} 
                    onChange={e => handleModalSubjectChange(e.target.value)} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">All Subjects</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: Topic, Sub Topic, Difficulty Level */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Topic</label>
                  <select 
                    value={filterTopicId} 
                    onChange={e => {
                      setFilterTopicId(e.target.value);
                      setFilterSubTopicId('');
                    }} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">Select Topic</option>
                    {topics.filter(t => {
                      if (modalFilters.question_type === 'Board Exams') {
                        if (t.exam_id) return false;
                        if (modalFilters.board_id && t.board_id && t.board_id != modalFilters.board_id) return false;
                      } else if (modalFilters.question_type === 'Compititive') {
                        if (t.board_id) return false;
                        if (modalFilters.exam_id && t.exam_id && t.exam_id != modalFilters.exam_id) return false;
                      } else {
                        if (modalFilters.board_id && t.board_id && t.board_id != modalFilters.board_id) return false;
                        if (modalFilters.exam_id && t.exam_id && t.exam_id != modalFilters.exam_id) return false;
                      }
                      if (modalFilters.class_id && t.class_id && t.class_id != modalFilters.class_id) return false;
                      if (modalFilters.subject_id && t.subject_id && t.subject_id != modalFilters.subject_id) return false;
                      return true;
                    }).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Sub Topic</label>
                  <select 
                    value={filterSubTopicId} 
                    onChange={e => setFilterSubTopicId(e.target.value)} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">Select Sub Topic</option>
                    {subTopics.filter(st => {
                      if (filterTopicId && st.topic_id && st.topic_id != filterTopicId) return false;
                      return true;
                    }).map(st => <option key={st.id} value={st.id}>{st.name}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Difficulty Level</label>
                  <select 
                    value={filterLevel} 
                    onChange={e => setFilterLevel(e.target.value)} 
                    style={{ width: '100%', padding: '11px 14px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                  >
                    <option value="">All Levels</option>
                    <option value="Easy">Easy</option>
                    <option value="Medium">Medium</option>
                    <option value="Hard">Hard</option>
                  </select>
                </div>
              </div>

              <div style={{ background: C.g50, padding: 20, borderRadius: 12, border: `1px dashed ${C.g300}`, minHeight: 200 }}>
                {!filterTopicId || !filterSubTopicId ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ margin: 0, color: C.g500, fontSize: 14, fontStyle: 'italic' }}>
                      Please select a Topic and Sub Topic to view available questions.
                    </p>
                  </div>
                ) : filteredAvailableQuestions.length === 0 ? (
                  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <p style={{ margin: 0, color: C.g500, fontSize: 14, fontStyle: 'italic' }}>
                      No questions found matching your filters.
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Select All Toolbar for Available Questions in Modal */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      background: '#fff',
                      borderRadius: 10,
                      border: `1px solid ${areAllModalQuestionsSelected || areSomeModalQuestionsSelected ? C.primary : C.g200}`,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                    }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: C.g700, userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={areAllModalQuestionsSelected}
                          ref={el => {
                            if (el) {
                              el.indeterminate = areSomeModalQuestionsSelected;
                            }
                          }}
                          onChange={handleSelectAllModalQuestions}
                          style={{ width: 17, height: 17, accentColor: C.primary, cursor: 'pointer' }}
                        />
                        Select All ({filteredAvailableQuestions.length} Questions)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: C.g500 }}>
                          Selected in list: <strong style={{ color: C.primary }}>{tempSelectedQuestions.filter(sq => filteredAvailableQuestions.some(q => q.id === sq.id)).length}</strong> / {filteredAvailableQuestions.length}
                        </span>
                        {(areSomeModalQuestionsSelected || areAllModalQuestionsSelected) && (
                          <button
                            type="button"
                            onClick={handleSelectAllModalQuestions}
                            style={{ padding: '4px 10px', background: C.g100, color: C.g600, border: `1px solid ${C.g300}`, borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}
                          >
                            {areAllModalQuestionsSelected ? 'Deselect All' : 'Select All'}
                          </button>
                        )}
                      </div>
                    </div>

                    {filteredAvailableQuestions.map((q) => {
                      const isSelected = tempSelectedQuestions.some(sq => sq.id === q.id);
                      return (
                        <label key={q.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, border: `1px solid ${isSelected ? C.primary : C.g200}`, borderRadius: 12, background: isSelected ? C.primaryGhost : '#fff', cursor: 'pointer', transition: 'all 0.2s' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTempQuestionSelection(q)}
                            style={{ marginTop: 2, width: 16, height: 16, accentColor: C.primary }}
                          />
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: '0 0 8px', fontSize: 14, color: C.g800, fontWeight: 500 }}>{q.question_text}</p>
                            <div style={{ display: 'flex', gap: 12, fontSize: 12, color: C.g500, alignItems: 'center' }}>
                              <span style={{ display: 'inline-block', padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 4, fontWeight: 600 }}>{getTopicName(q)}</span>
                              <span style={{ display: 'inline-block', padding: '2px 8px', background: C.g100, color: C.g700, borderRadius: 4 }}>{getSubTopicName(q)}</span>
                              <span style={{ display: 'inline-block', padding: '2px 8px', background: C.g100, color: C.g700, borderRadius: 4 }}>{q.level}</span>
                              <span style={{ marginLeft: 'auto' }}>Correct: <strong style={{ color: C.primary }}>{q.correct_answer}</strong></span>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{ padding: 24, borderTop: `1px solid ${C.g200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafafa', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <span style={{ fontSize: 14, color: C.g700, fontWeight: 600 }}>
                {tempSelectedQuestions.length} Questions Selected
              </span>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={closeModal} style={{ padding: '10px 20px', background: '#fff', color: C.g700, border: `1px solid ${C.g300}`, borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="button" onClick={confirmSelection} style={{ padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Add in list
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* View Question Modal */}
      {viewingQuestion && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: 600, borderRadius: 16, display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ padding: 24, borderBottom: `1px solid ${C.g200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, color: C.g900 }}>Question Details</h2>
              <button onClick={closeViewModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}>
                <X color={C.g500} size={24} />
              </button>
            </div>
            
            <div style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <span style={{ fontSize: 12, background: C.g100, color: C.g700, padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>{getTopicName(viewingQuestion)}</span>
                  <span style={{ fontSize: 12, background: C.g100, color: C.g700, padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>{getSubTopicName(viewingQuestion)}</span>
                  <span style={{ fontSize: 12, background: C.primaryGhost, color: C.primary, padding: '4px 10px', borderRadius: 12, fontWeight: 600 }}>{viewingQuestion.level}</span>
                </div>
                <h3 style={{ margin: '0 0 16px', fontSize: 16, color: C.g900, lineHeight: 1.5, fontWeight: 500 }}>
                  {viewingQuestion.question_text}
                </h3>
              </div>

              {viewingQuestion.format !== 'Subjective' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
                  {['A', 'B', 'C', 'D'].map(opt => {
                    const optionKey = `option_${opt.toLowerCase()}`;
                    if (!viewingQuestion[optionKey]) return null;
                    const isCorrect = viewingQuestion.correct_answer === opt;
                    return (
                      <div key={opt} style={{ padding: 12, border: `1px solid ${isCorrect ? '#10B981' : C.g200}`, borderRadius: 10, background: isCorrect ? '#ECFDF5' : '#fff', display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: isCorrect ? '#10B981' : C.g100, color: isCorrect ? '#fff' : C.g600, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600 }}>
                          {opt}
                        </div>
                        <span style={{ fontSize: 14, color: isCorrect ? '#065F46' : C.g800 }}>{viewingQuestion[optionKey]}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {viewingQuestion.format === 'Subjective' && (
                <div style={{ padding: 16, background: '#F8FAFC', borderRadius: 12, border: `1px solid ${C.g200}`, marginBottom: 24 }}>
                  <p style={{ margin: 0, fontSize: 14, color: C.g700 }}><em>This is a subjective question.</em></p>
                </div>
              )}

              {viewingQuestion.answer_desc && (
                <div>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14, color: C.g900 }}>Explanation</h4>
                  <div style={{ padding: 16, background: C.g50, borderRadius: 12, border: `1px solid ${C.g200}` }}>
                    <p style={{ margin: 0, fontSize: 14, color: C.g700, lineHeight: 1.5 }}>
                      {viewingQuestion.answer_desc}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ padding: 24, borderTop: `1px solid ${C.g200}`, display: 'flex', justifyContent: 'flex-end', background: '#fafafa', borderBottomLeftRadius: 16, borderBottomRightRadius: 16 }}>
              <button type="button" onClick={closeViewModal} style={{ padding: '10px 24px', background: C.g100, color: C.g700, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </AppLayout>
  );
};

export default QuizForm;
